/**
 * Background sync worker for queued offline mutations.
 *
 * - Subscribes to the browser `online` event.
 * - Drains the IndexedDB `sync_queue` table (oldest first).
 * - Routes each entry to its matching API endpoint based on `type`.
 * - On 200: removes the row. On error: increments attempts (drops to
 *   'failed' after 5 tries) so the UI can show & retry.
 * - Notifies listeners via `subscribe()` so the Topbar badge can update
 *   in real-time.
 */
import api from "./api";
import { listQueue, markSynced, markFailed, pendingCount, onQueueChanged } from "./offlineDb";

const listeners = new Set();
let running = false;

export function subscribe(cb) {
  listeners.add(cb);
  // Fire once with the current count so the UI hydrates.
  pendingCount().then((c) => cb({ pending: c, syncing: running }));
  return () => listeners.delete(cb);
}

async function notify() {
  const c = await pendingCount();
  listeners.forEach((cb) => cb({ pending: c, syncing: running }));
}

// Whenever offlineDb mutates the queue (enqueue / markSynced / markFailed /
// clearQueue), rebroadcast to our subscribers so UI badges stay live.
onQueueChanged(() => { notify(); });

async function postOne(row) {
  switch (row.type) {
    case "rto":
      return await api.post("/dispatch/rto", row.payload);
    case "tracking":
      return await api.post("/dispatch/tracking-import", row.payload);
    default:
      throw new Error(`Unknown sync type: ${row.type}`);
  }
}

export async function drainQueue() {
  if (running) return { processed: 0, skipped: true };
  running = true;
  await notify();
  let processed = 0;
  try {
    const queue = await listQueue("pending");
    for (const row of queue) {
      if (!navigator.onLine) break;
      try {
        await postOne(row);
        await markSynced(row.id);
        processed += 1;
      } catch (err) {
        await markFailed(row.id, err?.response?.data?.detail || err.message);
      }
      await notify();
    }
  } finally {
    running = false;
    await notify();
  }
  return { processed };
}

// Auto-bind to online/offline events.
let _bound = false;
export function installSyncWorker() {
  if (_bound) return;
  _bound = true;
  window.addEventListener("online", () => {
    drainQueue();
  });
  // Also drain once on boot in case the page loaded online with stale items.
  if (navigator.onLine) drainQueue();
}
